import apiFetch, { fetchCompanySettings } from 'src/Common/api/auth/Auth';
import { getCustomSettings } from 'src/Common/api/companySettingsApi';
import { DEVICE_DEFAULTS_AREA, pullDeviceSettings } from './deviceSettings';

// =====================================================================================
// Real-time settings refresh (SignalR "settings_changed").
//
// The server calls the hub client method SettingsChanged("<area>[,<area>…]") whenever a store
// screen or the Developer Settings module saves a setting; older servers sent the same list as
// ReceiveNotification("settings_changed", areas), still accepted silently (NotificationContext).
// The payload carries AREA NAMES only, never values, so this module re-reads exactly the client
// caches that hold those areas and then tells the open screens through window events:
//   - 'appThemeChanged'   ThemeProvider re-reads the company look      (existing event)
//   - 'posTerminalChanged' SidebarContext switches Simple / Terminal3   (existing event)
//   - 'settingsChanged'   generic, detail { areas: string[] }          (new; any screen may listen)
// 'device_defaults' (and '*', i.e. every reconnect) re-pulls this browser's managed device
// settings (deviceSettings.ts), which fires its own 'deviceSettingsApplied' event.
//
// Bursts (one save touching several areas, or several saves in a row) are coalesced: areas are
// accumulated and refreshed once, 500 ms after the last ping. A refresh never overlaps itself.
// =====================================================================================

export const SETTINGS_CHANGED_TITLE = 'settings_changed';

/** Window event fired after a refresh pass, detail `{ areas }` (`['*']` = everything). */
export const SETTINGS_CHANGED_EVENT = 'settingsChanged';

export interface SettingsChangedDetail {
  areas: string[];
}

/** Every area, used when the ping carries no area list (older server) and after a reconnect. */
export const ALL_SETTINGS_AREAS = '*';

const DEBOUNCE_MS = 500;

/** Areas whose values live (also) in the CompanySettings GET, i.e. sessionStorage.companySettings.
 *  The blob carries the CompanySettings columns plus a `customSettings` dict of every
 *  CompanyCustomSettings row, so any custom-setting area changes it too. */
const COMPANY_BLOB_AREAS = ['regional', 'company_profile', 'appearance', 'pos_flags', 'restaurant_flags', 'security_flags'];
/** Areas that may carry DefaultPOSTerminal (the Simple / Terminal3 switch). */
const POS_TERMINAL_AREAS = ['pos_flags', 'appearance'];

/** Split the SignalR message into clean, distinct, lower-case area names. */
export function parseSettingsAreas(message: unknown): string[] {
  if (typeof message !== 'string') return [];
  const seen = new Set<string>();
  message.split(',').forEach((part) => {
    const a = part.trim().toLowerCase();
    if (a) seen.add(a);
  });
  return Array.from(seen);
}

const getToken = () => sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');

/**
 * The developer "open store" popup keeps its token in sessionStorage but SHARES localStorage
 * (companyCountry, zatcaEnabled) with the developer's own window. Refreshing those keys from the
 * impersonated store would leak its values into the developer's session, so the popup refreshes
 * only tab-local state (sessionStorage) and fires the events.
 */
const isImpersonating = () => !!sessionStorage.getItem('impersonateToken');

const dispatch = (name: string, detail?: unknown) => {
  try {
    window.dispatchEvent(detail === undefined ? new Event(name) : new CustomEvent(name, { detail }));
  } catch {
    /* ignore */
  }
};

/** CompanySettings → sessionStorage.companySettings (+ 'appThemeChanged'). Never throws. */
async function refreshCompanySettingsBlob(): Promise<void> {
  if (!isImpersonating()) {
    // The login helper: writes the blob, fires appThemeChanged and fills companyCountry.
    await fetchCompanySettings();
    return;
  }
  try {
    const res: any = await apiFetch('CompanySettings', { method: 'GET' });
    if (res && res.settings) {
      sessionStorage.setItem('companySettings', JSON.stringify(res.settings));
      dispatch('appThemeChanged');
    }
  } catch {
    /* keep the current blob */
  }
}

/** DefaultPOSTerminal → 'posTerminalChanged' (SidebarContext normalizes the value). Never throws. */
async function refreshPosTerminal(): Promise<void> {
  try {
    const res: any = await getCustomSettings();
    const list = res?.customSettings;
    if (!Array.isArray(list)) return; // failed / unexpected shape: keep the current terminal
    const row = list.find((s: { settingKey?: string }) => s?.settingKey === 'DefaultPOSTerminal');
    // A missing row means the server default (Simple) — the same answer SidebarContext's own
    // route-change read gives, so the menu never disagrees with itself.
    dispatch('posTerminalChanged', { terminal: row?.settingValue ?? 'Simple' });
  } catch {
    /* keep the current terminal */
  }
}

/**
 * CompanyZatcaIntegrations → localStorage.zatcaEnabled, written ONLY on a successful answer (a
 * company with no ZATCA row gets a 200 with the flag off). Unlike the login helper
 * fetchZatcaStatus, a failed read — a network blip or the API warming up right after the
 * reconnect that queued this pass — keeps the current flag instead of switching ZATCA off for
 * the session. Never throws.
 */
async function refreshZatcaFlag(): Promise<void> {
  try {
    const data: any = await apiFetch('CompanyZatcaIntegrations', { method: 'GET' });
    if (!data || typeof data !== 'object') return; // unexpected body: keep the current flag
    const enabled = data.isZatcaIntegrationEnabled ?? data.IsZatcaIntegrationEnabled ?? false;
    localStorage.setItem('zatcaEnabled', enabled ? 'true' : 'false');
  } catch {
    /* keep the current flag */
  }
}

/** Receipt template: the print path caches the default template for 30 s. */
async function clearInvoiceTemplateCache(): Promise<void> {
  try {
    const { clearTemplateCache } = await import(
      'src/content/applications/SalesTerminal/Invoices/invoiceTemplateUtils'
    );
    clearTemplateCache();
  } catch {
    /* chunk unavailable (offline) — the 30 s cache expires on its own */
  }
}

/**
 * Re-read every client cache that holds one of `areas`, then fire 'settingsChanged'.
 * `['*']` (or an empty list) refreshes everything. Never throws.
 */
export async function refreshSettingsAreas(areas: string[]): Promise<void> {
  if (!getToken()) return;
  const all = areas.length === 0 || areas.includes(ALL_SETTINGS_AREAS);
  const has = (list: string[]) => all || list.some((a) => areas.includes(a));

  const tasks: Promise<void>[] = [];
  if (has(COMPANY_BLOB_AREAS)) tasks.push(refreshCompanySettingsBlob());
  if (has(POS_TERMINAL_AREAS)) tasks.push(refreshPosTerminal());
  if (has(['invoice_template'])) tasks.push(clearInvoiceTemplateCache());
  if (has(['zatca']) && !isImpersonating()) tasks.push(refreshZatcaFlag());
  // Managed device settings: GET resolved for this browser. A no-op outside a store session
  // (popup, developer, reseller); the device module serializes its own pulls, so it is not
  // awaited here — a slow pull never holds up the other areas.
  if (has([DEVICE_DEFAULTS_AREA])) void pullDeviceSettings();
  // 'fiscal' (FBR/PRA/SRB/KPRA), 'exchange_rules', 'label_templates', 'integrations',
  // 'notifications' and 'user_prefs' have no long-lived web cache: the screens fetch them on
  // open. Open screens that care listen to 'settingsChanged' (e.g. the Modern Dashboard re-reads
  // the user's layout on 'user_prefs').
  await Promise.all(tasks.map((t) => t.catch(() => undefined)));

  const detail: SettingsChangedDetail = { areas: all ? [ALL_SETTINGS_AREAS] : areas };
  dispatch(SETTINGS_CHANGED_EVENT, detail);
}

// ── Debounce / coalescing ───────────────────────────────────────────────────────────────

const pendingAreas = new Set<string>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let running = false;

function flush() {
  debounceTimer = null;
  if (running) return; // the running pass picks the pending areas up when it ends
  if (pendingAreas.size === 0) return;
  const areas = Array.from(pendingAreas);
  pendingAreas.clear();
  running = true;
  void refreshSettingsAreas(areas).finally(() => {
    running = false;
    if (pendingAreas.size > 0 && !debounceTimer) flush();
  });
}

/**
 * Queue a refresh for `areas` (empty = everything). Pings arriving within 500 ms of each other
 * are merged into one pass.
 */
export function queueSettingsRefresh(areas: string[]): void {
  if (areas.length === 0) pendingAreas.add(ALL_SETTINGS_AREAS);
  else areas.forEach((a) => pendingAreas.add(a));
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(flush, DEBOUNCE_MS);
}

/** Drop anything queued (logout / connection teardown). */
export function cancelQueuedSettingsRefresh(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  pendingAreas.clear();
}
